import { useEffect, useState, useCallback, useMemo } from 'react'
import { api, getAuthToken } from './api'
import { CategoriesView } from './components/CategoriesView'
import { DemoPriceListView } from './components/DemoPriceListView'
import { DemoProductsView } from './components/DemoProductsView'
import { DemoSemiFinishedView } from './components/DemoSemiFinishedView'
import { LoginView } from './components/LoginView'
import { PackagingsView } from './components/PackagingsView'
import { PriceListView } from './components/PriceListView'
import { ProductReportsView } from './components/ProductReportsView'
import { ProductsView } from './components/ProductsView'
import { RawMaterialReportView } from './components/RawMaterialReportView'
import { RawMaterialsView } from './components/RawMaterialsView'
import { SalesChannelsView } from './components/SalesChannelsView'
import { SemiFinishedView } from './components/SemiFinishedView'
import { SettingsView } from './components/SettingsView'
import { Sidebar } from './components/Sidebar'
import { TradingGoodsView } from './components/TradingGoodsView'
import parelLogo from '../assets/parelteknoloji-mavia.svg'
import {
  ActiveTab,
  Category,
  CurrentUserProfile,
  DemoProduct,
  DemoSemiFinishedProduct,
  Packaging,
  Product,
  RawMaterial,
  SalesChannel,
  SemiFinishedProduct,
  Tenant,
  TradingGood,
  User,
} from './types'

export type ActiveReport =
  | { type: 'product'; item: Product }
  | { type: 'semi_finished'; item: SemiFinishedProduct }
  | { type: 'demo_product'; item: DemoProduct }
  | { type: 'demo_semi_finished'; item: DemoSemiFinishedProduct }
  | { type: 'raw_material'; item: RawMaterial }

export function App() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('price-list')
  const [reportStack, setReportStack] = useState<ActiveReport[]>([])
  const currentReport = reportStack.length > 0 ? reportStack[reportStack.length - 1] : null

  const handleBackReport = useCallback(() => {
    setReportStack((prev) => prev.slice(0, -1))
  }, [])
  const [authChecking, setAuthChecking] = useState<boolean>(true)

  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null)

  const [categories, setCategories] = useState<Category[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [tradingGoods, setTradingGoods] = useState<TradingGood[]>([])
  const [semiFinished, setSemiFinished] = useState<SemiFinishedProduct[]>([])
  const [rawMaterials, setRawMaterials] = useState<RawMaterial[]>([])
  const [packagings, setPackagings] = useState<Packaging[]>([])
  const [salesChannels, setSalesChannels] = useState<SalesChannel[]>([])
  const [demoProducts, setDemoProducts] = useState<DemoProduct[]>([])
  const [demoSemiFinished, setDemoSemiFinished] = useState<DemoSemiFinishedProduct[]>([])
  const [_loading, setLoading] = useState<boolean>(false)
  const [dataLoadErrors, setDataLoadErrors] = useState<string[]>([])

  // Load tenant-scoped data
  const loadData = useCallback(async () => {
    setLoading(true)
    setDataLoadErrors([])
    try {
      const results = await Promise.allSettled([
        api.listCategories(),
        api.listProducts(),
        api.listTradingGoods(),
        api.listSemiFinished(),
        api.listRawMaterials(),
        api.listPackagings(),
        api.listSalesChannels(),
        api.listDemoProducts(),
        api.listDemoSemiFinished(),
      ])

      const endpointNames = [
        'Kategoriler',
        'Mamul Ürünler',
        'Ticari Mallar',
        'Yarı Mamuller',
        'Hammaddeler',
        'Ambalajlar',
        'Satış Kanalları',
        'Demo Ürünler',
        'Demo Yarı Mamuller',
      ]
      const errors: string[] = []
      results.forEach((res, i) => {
        if (res.status === 'rejected') {
          errors.push(`${endpointNames[i]}: ${res.reason?.message || 'Bilinmeyen hata'}`)
        }
      })

      if (results[0].status === 'fulfilled') setCategories(results[0].value)
      if (results[1].status === 'fulfilled') setProducts(results[1].value)
      if (results[2].status === 'fulfilled') setTradingGoods(results[2].value)
      if (results[3].status === 'fulfilled') setSemiFinished(results[3].value)
      if (results[4].status === 'fulfilled') setRawMaterials(results[4].value)
      if (results[5].status === 'fulfilled') setPackagings(results[5].value)
      if (results[6].status === 'fulfilled') setSalesChannels(results[6].value)

      // Ensure demo products have components populated
      if (results[7].status === 'fulfilled') {
        const rawDemoList = results[7].value
        const demoList = await Promise.all(
          rawDemoList.map(async (dp) => {
            if (dp.components && Array.isArray(dp.components) && dp.components.length > 0) {
              return dp
            }
            if ((dp.components_count ?? 0) > 0 || !dp.components) {
              try {
                const full = await api.getDemoProduct(dp.id)
                return full
              } catch {
                return dp
              }
            }
            return dp
          })
        )
        setDemoProducts(demoList)
      }

      // Ensure demo semi-finished products have components populated
      if (results[8].status === 'fulfilled') {
        const rawDemoSfList = results[8].value
        const demoSfList = await Promise.all(
          rawDemoSfList.map(async (dsf) => {
            if (dsf.components && Array.isArray(dsf.components) && dsf.components.length > 0) {
              return dsf
            }
            if ((dsf.components_count ?? 0) > 0 || !dsf.components) {
              try {
                const full = await api.getDemoSemiFinished(dsf.id)
                return full
              } catch {
                return dsf
              }
            }
            return dsf
          })
        )
        setDemoSemiFinished(demoSfList)
      }

      if (errors.length > 0) {
        setDataLoadErrors(errors)
      }
    } catch (err: any) {
      console.error('Failed to load tenant data:', err)
      setDataLoadErrors([err?.message || 'Veriler yüklenirken beklenmeyen bir hata oluştu.'])
    } finally {
      setLoading(false)
    }
  }, [])

  // Granular targeted refresh functions for specific entities
  const refreshCategories = useCallback(async () => {
    try {
      const list = await api.listCategories()
      setCategories(list)
    } catch (err) {
      console.error('Failed to refresh categories:', err)
    }
  }, [])

  const refreshProducts = useCallback(async () => {
    try {
      const list = await api.listProducts()
      setProducts(list)
    } catch (err) {
      console.error('Failed to refresh products:', err)
    }
  }, [])

  const refreshTradingGoods = useCallback(async () => {
    try {
      const list = await api.listTradingGoods()
      setTradingGoods(list)
    } catch (err) {
      console.error('Failed to refresh trading goods:', err)
    }
  }, [])

  const refreshSemiFinished = useCallback(async () => {
    try {
      const list = await api.listSemiFinished()
      setSemiFinished(list)
    } catch (err) {
      console.error('Failed to refresh semi-finished:', err)
    }
  }, [])

  const refreshRawMaterials = useCallback(async () => {
    try {
      const list = await api.listRawMaterials()
      setRawMaterials(list)
    } catch (err) {
      console.error('Failed to refresh raw materials:', err)
    }
  }, [])

  const refreshPackagings = useCallback(async () => {
    try {
      const list = await api.listPackagings()
      setPackagings(list)
    } catch (err) {
      console.error('Failed to refresh packagings:', err)
    }
  }, [])

  const refreshSalesChannels = useCallback(async () => {
    try {
      const list = await api.listSalesChannels()
      setSalesChannels(list)
    } catch (err) {
      console.error('Failed to refresh sales channels:', err)
    }
  }, [])

  const refreshDemoProducts = useCallback(async () => {
    try {
      const rawDemoList = await api.listDemoProducts()
      const demoList = await Promise.all(
        rawDemoList.map(async (dp) => {
          if (dp.components && Array.isArray(dp.components) && dp.components.length > 0) {
            return dp
          }
          if ((dp.components_count ?? 0) > 0 || !dp.components) {
            try {
              const full = await api.getDemoProduct(dp.id)
              return full
            } catch {
              return dp
            }
          }
          return dp
        })
      )
      setDemoProducts(demoList)
    } catch (err) {
      console.error('Failed to refresh demo products:', err)
    }
  }, [])

  const refreshDemoSemiFinished = useCallback(async () => {
    try {
      const rawDemoSfList = await api.listDemoSemiFinished()
      const demoSfList = await Promise.all(
        rawDemoSfList.map(async (dsf) => {
          if (dsf.components && Array.isArray(dsf.components) && dsf.components.length > 0) {
            return dsf
          }
          if ((dsf.components_count ?? 0) > 0 || !dsf.components) {
            try {
              const full = await api.getDemoSemiFinished(dsf.id)
              return full
            } catch {
              return dsf
            }
          }
          return dsf
        })
      )
      setDemoSemiFinished(demoSfList)
    } catch (err) {
      console.error('Failed to refresh demo semi-finished:', err)
    }
  }, [])

  // Targeted cascading refreshers for multi-level Excel imports
  const refreshProductImportCascade = useCallback(async () => {
    try {
      const [prodList, sfList, rmList] = await Promise.all([
        api.listProducts(),
        api.listSemiFinished(),
        api.listRawMaterials(),
      ])
      setProducts(prodList)
      setSemiFinished(sfList)
      setRawMaterials(rmList)
    } catch (err) {
      console.error('Failed cascade refresh on product excel import:', err)
    }
  }, [])

  const refreshSemiFinishedImportCascade = useCallback(async () => {
    try {
      const [sfList, rmList] = await Promise.all([
        api.listSemiFinished(),
        api.listRawMaterials(),
      ])
      setSemiFinished(sfList)
      setRawMaterials(rmList)
    } catch (err) {
      console.error('Failed cascade refresh on semi-finished excel import:', err)
    }
  }, [])

  const productsMap = useMemo(() => new Map(products.map((p) => [p.id, p])), [products])
  const semiFinishedMap = useMemo(() => new Map(semiFinished.map((s) => [s.id, s])), [semiFinished])
  const rawMaterialsMap = useMemo(() => new Map(rawMaterials.map((r) => [r.id, r])), [rawMaterials])
  const packagingsMap = useMemo(() => new Map(packagings.map((p) => [p.id, p])), [packagings])
  const tradingGoodsMap = useMemo(() => new Map(tradingGoods.map((tg) => [tg.id, tg])), [tradingGoods])
  const demoProductsMap = useMemo(() => new Map(demoProducts.map((dp) => [dp.id, dp])), [demoProducts])
  const demoSemiFinishedMap = useMemo(() => new Map(demoSemiFinished.map((ds) => [ds.id, ds])), [demoSemiFinished])

  const currentReportResolved = useMemo(() => {
    if (!currentReport) return null
    if (currentReport.type === 'product') {
      return {
        type: 'product' as const,
        item: products.find((p) => p.id === currentReport.item.id) || currentReport.item,
      }
    }
    if (currentReport.type === 'semi_finished') {
      return {
        type: 'semi_finished' as const,
        item: semiFinished.find((s) => s.id === currentReport.item.id) || currentReport.item,
      }
    }
    if (currentReport.type === 'raw_material') {
      return {
        type: 'raw_material' as const,
        item: rawMaterials.find((r) => r.id === currentReport.item.id) || currentReport.item,
      }
    }
    if (currentReport.type === 'demo_product') {
      return {
        type: 'demo_product' as const,
        item: demoProducts.find((dp) => dp.id === currentReport.item.id) || currentReport.item,
      }
    }
    return {
      type: 'demo_semi_finished' as const,
      item: demoSemiFinished.find((ds) => ds.id === currentReport.item.id) || currentReport.item,
    }
  }, [currentReport, products, semiFinished, rawMaterials, demoProducts, demoSemiFinished])

  // Check auth on initial mount
  useEffect(() => {
    const token = getAuthToken()
    if (!token) {
      setAuthChecking(false)
      return
    }

    api
      .getMe()
      .then((profile) => {
        setCurrentUser(profile.user)
        setCurrentTenant(profile.tenant)
        loadData()
      })
      .catch(() => {
        api.logout()
        setCurrentUser(null)
        setCurrentTenant(null)
      })
      .finally(() => {
        setAuthChecking(false)
      })
  }, [loadData])

  // Ensure non-admins cannot stay on restricted settings tab
  useEffect(() => {
    if (currentUser && currentUser.role !== 'tenant_admin' && activeTab === 'settings') {
      setActiveTab('price-list')
    }
  }, [currentUser, activeTab])

  const handleLoginSuccess = (profile: CurrentUserProfile) => {
    setCurrentUser(profile.user)
    setCurrentTenant(profile.tenant)
    loadData()
  }

  const refreshTenant = useCallback(async () => {
    try {
      const t = await api.getCurrentTenant()
      setCurrentTenant(t)
    } catch {
      // ignore
    }
  }, [])

  const handleLogout = () => {
    api.logout()
    setCurrentUser(null)
    setCurrentTenant(null)
    setReportStack([])
    setCategories([])
    setProducts([])
    setTradingGoods([])
    setSemiFinished([])
    setRawMaterials([])
    setPackagings([])
    setSalesChannels([])
    setDemoProducts([])
    setDemoSemiFinished([])
  }

  if (authChecking) {
    return (
      <div className="loading-splash">
        <div className="loading-spinner" aria-hidden="true" />
        <span>Oturum kontrol ediliyor…</span>
      </div>
    )
  }

  if (!currentUser) {
    return <LoginView onLoginSuccess={handleLoginSuccess} />
  }

  const tabTitles: Record<ActiveTab, { title: string; category: string }> = {
    'price-list': { title: 'Fiyat Listeleri', category: 'Fiyatlandırma & Raporlar' },
    'products': { title: 'Mamul Ürünler', category: 'Ürün Kataloğu' },
    'trading-goods': { title: 'Al-Sat Ticari Ürünler', category: 'Ürün Kataloğu' },
    'semi-finished': { title: 'Yarı Mamuller', category: 'Ürün Kataloğu' },
    'demo-products': { title: 'Demo Mamul Ürünler', category: 'Demo' },
    'demo-semi-finished': { title: 'Demo Yarı Mamuller', category: 'Demo' },
    'demo-price-list': { title: 'Demo Fiyat Listeleri', category: 'Demo' },
    'raw-materials': { title: 'Hammaddeler', category: 'Malzeme & Bileşenler' },
    'packagings': { title: 'Ambalaj Malzemeleri', category: 'Malzeme & Bileşenler' },
    'categories': { title: 'Kategoriler & Varsayılan Gider Kuralları', category: 'Sistem & Parametreler' },
    'sales-channels': { title: 'Satış Kanalları & Kâr Marjları', category: 'Sistem & Parametreler' },
    'settings': { title: 'Firma ve Sistem Ayarları', category: 'Sistem & Parametreler' },
  }

  const currentTabInfo = currentReport
    ? {
        title:
          currentReport.type === 'raw_material'
            ? `${currentReport.item.name} (${currentReport.item.code}) Hammadde Raporu`
            : `${currentReport.item.name} (${(currentReport.item as any).code || 'DEMO'}) Raporları`,
        category:
          currentReport.type === 'raw_material'
            ? 'Hammaddeler'
            : currentReport.type === 'product'
            ? 'Mamul Ürünler'
            : currentReport.type === 'semi_finished'
            ? 'Yarı Mamuller'
            : currentReport.type === 'demo_product'
            ? 'Demo Mamul Ürünler'
            : 'Demo Yarı Mamuller',
      }
    : tabTitles[activeTab] || { title: '', category: '' }

  return (
    <div className="app-layout">
      {/* Left Navigation Sidebar */}
      <Sidebar
        activeTab={activeTab}
        onSelectTab={(tab) => {
          setReportStack([])
          setActiveTab(tab)
        }}
        tenant={currentTenant}
        user={currentUser}
        onLogout={handleLogout}
        counts={{
          products: products.length,
          tradingGoods: tradingGoods.length,
          semiFinished: semiFinished.length,
          rawMaterials: rawMaterials.length,
          packagings: packagings.length,
          categories: categories.length,
          salesChannels: salesChannels.length,
          demoProducts: demoProducts.length,
          demoSemiFinished: demoSemiFinished.length,
        }}
      />

      {/* Main App Container */}
      <div className="app-container">
        {/* Top Header */}
        <header className="app-topbar no-print">
          <div className="topbar-breadcrumbs">
            <span className="breadcrumb-category">{currentTabInfo.category}</span>
            <span className="breadcrumb-separator" aria-hidden="true">/</span>
            <h1 className="breadcrumb-title">{currentTabInfo.title}</h1>
          </div>

          <div className="topbar-actions">
            <img
              src={parelLogo}
              alt="Parel Teknoloji"
              className="topbar-brand-icon"
            />
          </div>
        </header>

        {/* Main View Area */}
        <main className="app-main-content">
          {dataLoadErrors.length > 0 && (
            <div
              style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #f87171',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                color: '#991b1b',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong style={{ fontSize: '1rem', display: 'block', marginBottom: '0.25rem' }}>
                    ⚠️ Bazı veriler yüklenirken hata oluştu:
                  </strong>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem' }}>
                    {dataLoadErrors.map((errMsg, idx) => (
                      <li key={idx}>{errMsg}</li>
                    ))}
                  </ul>
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ whiteSpace: 'nowrap', marginLeft: '1rem' }}
                  onClick={() => loadData()}
                >
                  Yeniden Dene
                </button>
              </div>
            </div>
          )}
          {currentReportResolved ? (
            currentReportResolved.type === 'raw_material' ? (
              <RawMaterialReportView
                rawMaterial={currentReportResolved.item}
                products={products}
                semiFinished={semiFinished}
                categories={categories}
                productsMap={productsMap}
                semiFinishedMap={semiFinishedMap}
                onBack={handleBackReport}
                onOpenReport={(item, type) => setReportStack((prev) => [...prev, { item, type } as ActiveReport])}
              />
            ) : (
              <ProductReportsView
                product={currentReportResolved.item}
                productType={currentReportResolved.type}
                categories={categories}
                productsMap={productsMap}
                semiFinishedMap={semiFinishedMap}
                rawMaterialsMap={rawMaterialsMap}
                packagingsMap={packagingsMap}
                tradingGoodsMap={tradingGoodsMap}
                demoProductsMap={demoProductsMap}
                demoSemiFinishedMap={demoSemiFinishedMap}
                onBack={handleBackReport}
                onOpenReport={(item, type) => setReportStack((prev) => [...prev, { item, type } as ActiveReport])}
              />
            )
          ) : (
            <>
              {activeTab === 'price-list' && (
                <PriceListView salesChannels={salesChannels} categories={categories} tenant={currentTenant} />
              )}

              {activeTab === 'products' && (
                <ProductsView
                  products={products}
                  setProducts={setProducts}
                  setDemoProducts={setDemoProducts}
                  categories={categories}
                  rawMaterials={rawMaterials}
                  packagings={packagings}
                  tradingGoods={tradingGoods}
                  semiFinished={semiFinished}
                  onRefresh={refreshProducts}
                  onRefreshProducts={refreshProducts}
                  onCascadeRefresh={refreshProductImportCascade}
                  onOpenReport={(item, type) => setReportStack([{ item, type: (type as any) || 'product' }])}
                />
              )}

              {activeTab === 'trading-goods' && (
                <TradingGoodsView
                  tradingGoods={tradingGoods}
                  setTradingGoods={setTradingGoods}
                  categories={categories}
                  onRefresh={refreshTradingGoods}
                />
              )}

              {activeTab === 'semi-finished' && (
                <SemiFinishedView
                  semiFinished={semiFinished}
                  setSemiFinished={setSemiFinished}
                  setDemoSemiFinished={setDemoSemiFinished}
                  products={products}
                  rawMaterials={rawMaterials}
                  packagings={packagings}
                  tradingGoods={tradingGoods}
                  categories={categories}
                  onRefresh={refreshSemiFinished}
                  onRefreshSemiFinished={refreshSemiFinished}
                  onCascadeRefresh={refreshSemiFinishedImportCascade}
                  onOpenReport={(item, type) => setReportStack([{ item, type: (type as any) || 'semi_finished' }])}
                />
              )}

              {activeTab === 'demo-products' && (
                <DemoProductsView
                  demoProducts={demoProducts}
                  setDemoProducts={setDemoProducts}
                  setProducts={setProducts}
                  demoSemiFinished={demoSemiFinished}
                  products={products}
                  categories={categories}
                  rawMaterials={rawMaterials}
                  packagings={packagings}
                  tradingGoods={tradingGoods}
                  semiFinished={semiFinished}
                  onRefresh={refreshDemoProducts}
                  onOpenReport={(item, type) => setReportStack([{ item, type: (type as any) || 'demo_product' }])}
                />
              )}

              {activeTab === 'demo-semi-finished' && (
                <DemoSemiFinishedView
                  demoSemiFinished={demoSemiFinished}
                  setDemoSemiFinished={setDemoSemiFinished}
                  setSemiFinished={setSemiFinished}
                  demoProducts={demoProducts}
                  products={products}
                  categories={categories}
                  rawMaterials={rawMaterials}
                  packagings={packagings}
                  tradingGoods={tradingGoods}
                  semiFinished={semiFinished}
                  onRefresh={refreshDemoSemiFinished}
                  onOpenReport={(item, type) => setReportStack([{ item, type: (type as any) || 'demo_semi_finished' }])}
                />
              )}

              {activeTab === 'demo-price-list' && (
                <DemoPriceListView salesChannels={salesChannels} categories={categories} tenant={currentTenant} />
              )}

              {activeTab === 'raw-materials' && (
                <RawMaterialsView
                  rawMaterials={rawMaterials}
                  setRawMaterials={setRawMaterials}
                  categories={categories}
                  onRefresh={refreshRawMaterials}
                  onOpenReport={(item) => setReportStack([{ item, type: 'raw_material' }])}
                />
              )}

              {activeTab === 'packagings' && (
                <PackagingsView
                  packagings={packagings}
                  setPackagings={setPackagings}
                  categories={categories}
                  onRefresh={refreshPackagings}
                />
              )}

              {activeTab === 'categories' && (
                <CategoriesView
                  categories={categories}
                  setCategories={setCategories}
                  onRefresh={refreshCategories}
                  tenant={currentTenant}
                />
              )}

              {activeTab === 'sales-channels' && (
                <SalesChannelsView
                  salesChannels={salesChannels}
                  setSalesChannels={setSalesChannels}
                  categories={categories}
                  onRefresh={refreshSalesChannels}
                />
              )}

              {activeTab === 'settings' && currentTenant && currentUser && currentUser.role === 'tenant_admin' && (
                <SettingsView
                  tenant={currentTenant}
                  currentUser={currentUser}
                  onTenantUpdated={refreshTenant}
                />
              )}
            </>
          )}
        </main>
      </div>
    </div>
  )
}

export default App
